import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { BarChartHoverLabel } from '@/components/admin/BarChartHoverLabel';
import { apiJson } from '@/lib/api';
import { track } from '@/lib/analytics';
import {
  formatPacificCivilDate,
  formatPacificDateTime,
} from '@/lib/adminPacificTime';
import type {
  AdminBookmarkDailyPoint,
  AdminBookmarkMetricsRange,
  AdminBookmarkMetricsResponse,
  AdminBookmarkRow,
  AdminBookmarksListResponse,
  AdminChatType,
} from '@/lib/types';

const PAGE_SIZE = 50;
const BRAND_BAR = '#018A9D';

const RANGE_OPTIONS: { range: AdminBookmarkMetricsRange; label: string }[] = [
  { range: '7d', label: '7d' },
  { range: '30d', label: '30d' },
  { range: '90d', label: '90d' },
];

const ALLOWED_RANGES = new Set<string>(RANGE_OPTIONS.map((r) => r.range));

const ADMIN_USER_LINK_CLASS =
  'inline-flex px-2 py-1 rounded-md border border-gray-300 text-xs font-semibold text-gray-700 hover:bg-gray-100';

function authorLabel(
  author: { first_name: string | null; last_name: string | null },
  userId: number,
): string {
  const n = [author.first_name, author.last_name].filter(Boolean).join(' ').trim();
  return n || `User #${userId}`;
}

function typeLabel(chatType: AdminChatType): string {
  switch (chatType) {
    case 'main':
      return 'Main';
    case 'custom':
      return 'Custom';
    case 'expense-dispute':
      return 'Expense dispute';
    case 'payment-dispute':
      return 'Payment dispute';
  }
}

function typeBadgeClass(chatType: AdminChatType): string {
  switch (chatType) {
    case 'main':
      return 'bg-sky-100 text-sky-800 border-sky-200';
    case 'custom':
      return 'bg-violet-100 text-violet-800 border-violet-200';
    case 'expense-dispute':
      return 'bg-amber-100 text-amber-900 border-amber-200';
    case 'payment-dispute':
      return 'bg-orange-100 text-orange-900 border-orange-200';
  }
}

function niceAxisMax(value: number): number {
  if (value <= 0) return 5;
  if (value <= 5) return 5;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const normalized = value / magnitude;
  const nice =
    normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return nice * magnitude;
}

function yAxisTicks(max: number): number[] {
  const top = niceAxisMax(max);
  const step =
    top <= 5 ? 1 : top <= 10 ? 2 : top <= 25 ? 5 : top <= 50 ? 10 : Math.ceil(top / 4 / 10) * 10;
  const ticks: number[] = [];
  for (let v = 0; v <= top; v += step) ticks.push(v);
  if (ticks[ticks.length - 1] !== top) ticks.push(top);
  return ticks;
}

function xLabelIndices(count: number): number[] {
  if (count <= 1) return [0];
  if (count <= 8) return Array.from({ length: count }, (_, i) => i);
  const targetLabels = count <= 31 ? 7 : 10;
  const step = Math.max(1, Math.round((count - 1) / (targetLabels - 1)));
  const indices: number[] = [0];
  for (let i = step; i < count - 1; i += step) indices.push(i);
  if (indices[indices.length - 1] !== count - 1) indices.push(count - 1);
  return indices;
}

function DailyBookmarkChart({ daily }: { daily: AdminBookmarkDailyPoint[] }) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const n = daily.length;
  const maxValue = Math.max(0, ...daily.map((d) => d.bookmarks));
  const axisMax = niceAxisMax(maxValue);
  const ticks = yAxisTicks(maxValue);
  const labelIndices = useMemo(() => new Set(xLabelIndices(n)), [n]);
  const showYearOnAxis =
    n > 0 && daily[0]?.date.slice(0, 4) !== daily[n - 1]?.date.slice(0, 4);

  const height = 240;
  const padLeft = 44;
  const padRight = 12;
  const padTop = 18;
  const padBottom = 36;
  const innerW = 720;
  const innerH = height - padTop - padBottom;
  const width = padLeft + innerW + padRight;
  const slotW = n > 0 ? innerW / n : innerW;
  const barGap = Math.min(6, slotW * 0.3);
  const barW = Math.max(1, slotW - barGap);
  const baselineY = height - padBottom;

  if (!n) {
    return <p className='text-sm text-gray-500'>No daily data in this date range.</p>;
  }

  const hovered = hoveredIndex !== null ? daily[hoveredIndex] : null;

  return (
    <div className='min-w-0' onMouseLeave={() => setHoveredIndex(null)}>
      <div className='mb-3 flex flex-wrap items-baseline justify-between gap-2'>
        <div>
          <p className='text-sm font-semibold text-gray-900'>Bookmarks created</p>
          <p className='text-xs text-gray-500'>Pacific calendar day · unique bookmarkers on hover</p>
        </div>
        <p className='text-xs text-gray-500 tabular-nums'>
          {formatPacificCivilDate(daily[0].date)} –{' '}
          {formatPacificCivilDate(daily[n - 1].date, { year: 'numeric' })}
        </p>
      </div>
      <div className='rounded-md border border-gray-100 bg-gray-50/50'>
        <svg
          viewBox={`0 0 ${width} ${height}`}
          width='100%'
          height={height}
          role='img'
          aria-label='Bookmarks created per day'
        >
          {ticks.map((tick) => {
            const y = baselineY - (tick / axisMax) * innerH;
            return (
              <g key={tick}>
                <line
                  x1={padLeft}
                  y1={y}
                  x2={padLeft + innerW}
                  y2={y}
                  stroke={tick === 0 ? '#d1d5db' : '#e5e7eb'}
                  strokeDasharray={tick === 0 ? undefined : '4 4'}
                />
                <text
                  x={padLeft - 8}
                  y={y + 4}
                  textAnchor='end'
                  className='fill-gray-500 text-[10px] tabular-nums'
                >
                  {tick.toLocaleString()}
                </text>
              </g>
            );
          })}

          {daily.map((d, i) => {
            const h = axisMax > 0 ? (d.bookmarks / axisMax) * innerH : 0;
            const x = padLeft + i * slotW + barGap / 2;
            const y = baselineY - h;
            const cx = x + barW / 2;
            const showXLabel = labelIndices.has(i);
            const isHovered = hoveredIndex === i;
            return (
              <g key={d.date}>
                <rect
                  x={padLeft + i * slotW}
                  y={padTop}
                  width={slotW}
                  height={innerH}
                  fill='transparent'
                  className='cursor-pointer'
                  onMouseEnter={() => setHoveredIndex(i)}
                />
                <rect
                  x={x}
                  y={y}
                  width={barW}
                  height={Math.max(0, h)}
                  fill={BRAND_BAR}
                  fillOpacity={isHovered ? 1 : hoveredIndex !== null ? 0.55 : 0.85}
                  rx={2}
                  className='pointer-events-none'
                />
                {showXLabel ? (
                  <text
                    x={cx}
                    y={height - 12}
                    textAnchor='middle'
                    className='fill-gray-500 text-[10px]'
                  >
                    {formatPacificCivilDate(d.date, {
                      year: showYearOnAxis ? '2-digit' : undefined,
                    })}
                  </text>
                ) : null}
              </g>
            );
          })}

          {hovered && hoveredIndex !== null ? (
            <BarChartHoverLabel
              x={padLeft + hoveredIndex * slotW + slotW / 2}
              barTopY={
                baselineY -
                (axisMax > 0 ? (hovered.bookmarks / axisMax) * innerH : 0)
              }
              text={`${hovered.bookmarks.toLocaleString()} · ${hovered.unique_users.toLocaleString()} users`}
              minX={padLeft + 40}
              maxX={padLeft + innerW - 40}
            />
          ) : null}
        </svg>
      </div>
    </div>
  );
}

function MixBar({
  label,
  count,
  total,
  className,
}: {
  label: string;
  count: number;
  total: number;
  className: string;
}) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div>
      <div className='flex items-baseline justify-between gap-3 text-sm'>
        <span className='font-medium text-gray-800'>{label}</span>
        <span className='tabular-nums text-gray-600'>
          {count.toLocaleString()} · {pct}%
        </span>
      </div>
      <div className='mt-1 h-2 rounded-full bg-gray-100 overflow-hidden'>
        <div
          className={`h-full rounded-full ${className}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function AdminBookmarksPage() {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const { range: rangeParam } = Route.useSearch();
  const range: AdminBookmarkMetricsRange = ALLOWED_RANGES.has(rangeParam ?? '')
    ? (rangeParam as AdminBookmarkMetricsRange)
    : '30d';
  const [page, setPage] = useState(0);
  const [householdFilter, setHouseholdFilter] = useState('');
  const [chatTypeFilter, setChatTypeFilter] = useState<AdminChatType | ''>('');

  useEffect(() => {
    track('Admin Bookmarks Screen');
  }, []);

  const householdIdParam = householdFilter.trim();
  const householdId =
    householdIdParam && /^\d+$/.test(householdIdParam) ? Number(householdIdParam) : null;

  const metricsQuery = useQuery({
    queryKey: ['admin', 'bookmarks', 'metrics', range],
    queryFn: () =>
      apiJson<AdminBookmarkMetricsResponse>(
        getToken,
        `/v1/admin/bookmarks/metrics?range=${range}`,
      ),
    refetchOnWindowFocus: true,
  });

  const listQuery = useQuery({
    queryKey: [
      'admin',
      'bookmarks',
      'list',
      page,
      householdIdParam,
      chatTypeFilter,
    ],
    queryFn: () => {
      const params = new URLSearchParams();
      params.set('limit', String(PAGE_SIZE));
      params.set('offset', String(page * PAGE_SIZE));
      if (householdId != null) params.set('household_id', String(householdId));
      if (chatTypeFilter) params.set('chat_type', chatTypeFilter);
      return apiJson<AdminBookmarksListResponse>(
        getToken,
        `/v1/admin/bookmarks?${params.toString()}`,
      );
    },
    placeholderData: keepPreviousData,
  });

  const metrics = metricsQuery.data;
  const rows = listQuery.data?.bookmarks ?? [];
  const total = listQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const setRange = (next: AdminBookmarkMetricsRange) => {
    navigate({ to: '/admin/bookmarks', search: { range: next } });
  };

  return (
    <div className='space-y-8'>
      <div>
        <h1 className='text-2xl font-semibold text-gray-900'>Bookmarks</h1>
        <p className='mt-2 text-sm text-gray-600'>
          Private saved chat messages: adoption, mix by thread type, custom folders,
          and a recent feed (preview only).
        </p>
      </div>

      <div className='flex flex-wrap items-center gap-3'>
        <div className='inline-flex rounded-lg border border-gray-200 bg-white p-1'>
          {RANGE_OPTIONS.map((opt) => {
            const active = opt.range === range;
            return (
              <button
                key={opt.range}
                type='button'
                onClick={() => setRange(opt.range)}
                className={`min-w-[3rem] rounded-md px-3 py-2 text-xs font-bold transition-all ${
                  active
                    ? 'bg-primary text-white shadow-sm ring-2 ring-primary/30'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
        <button
          type='button'
          onClick={() => {
            void metricsQuery.refetch();
            void listQuery.refetch();
          }}
          className='px-3 py-2 rounded-md border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50'
        >
          Refresh
        </button>
        {metricsQuery.isFetching || listQuery.isFetching ? (
          <span className='text-xs text-gray-500'>Updating…</span>
        ) : null}
      </div>

      {metricsQuery.isLoading && !metrics ? (
        <div className='flex justify-center py-12'>
          <div className='animate-spin h-10 w-10 border-2 border-primary border-t-transparent rounded-full' />
        </div>
      ) : metricsQuery.isError ? (
        <p className='text-sm text-red-600'>Could not load bookmark metrics.</p>
      ) : metrics ? (
        <>
          <div className='grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3'>
            <div className='rounded-lg border border-gray-200 bg-white p-4 shadow-sm'>
              <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
                Bookmarks
              </p>
              <p className='mt-1 text-3xl font-semibold tabular-nums text-gray-900'>
                {metrics.bookmark_count.toLocaleString()}
              </p>
              <p className='mt-1 text-xs text-gray-500'>
                {metrics.default_folder_bookmarks.toLocaleString()} default ·{' '}
                {metrics.custom_folder_bookmarks.toLocaleString()} custom
              </p>
            </div>
            <div className='rounded-lg border border-primary/30 bg-harbor-50 p-4 shadow-sm'>
              <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
                People · households
              </p>
              <p className='mt-1 text-3xl font-semibold tabular-nums text-gray-900'>
                {metrics.user_count.toLocaleString()}
                <span className='text-lg font-normal text-gray-400'> · </span>
                {metrics.household_count.toLocaleString()}
              </p>
              <p className='mt-1 text-xs text-gray-500'>
                With at least one saved message
              </p>
            </div>
            <div className='rounded-lg border border-gray-200 bg-white p-4 shadow-sm'>
              <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
                Custom folders
              </p>
              <p className='mt-1 text-3xl font-semibold tabular-nums text-gray-900'>
                {metrics.custom_folder_count.toLocaleString()}
              </p>
              <p className='mt-1 text-xs text-gray-500'>
                {metrics.users_with_custom_folders.toLocaleString()} people ·{' '}
                {metrics.folder_count.toLocaleString()} folders total
              </p>
            </div>
            <div className='rounded-lg border border-gray-200 bg-white p-4 shadow-sm'>
              <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
                New bookmarks
              </p>
              <p className='mt-1 text-2xl font-semibold tabular-nums text-gray-900'>
                {metrics.bookmarks_last_7_days.toLocaleString()} / 7d
              </p>
              <p className='mt-1 text-sm tabular-nums text-gray-700'>
                {metrics.bookmarks_last_30_days.toLocaleString()} in last 30 days
              </p>
            </div>
          </div>

          <section className='rounded-lg border border-gray-200 bg-white p-4 shadow-sm sm:p-6'>
            <DailyBookmarkChart daily={metrics.daily} />
          </section>

          <div className='grid grid-cols-1 lg:grid-cols-3 gap-4'>
            <section className='rounded-lg border border-gray-200 bg-white p-4 shadow-sm space-y-3'>
              <h2 className='text-sm font-semibold text-gray-900'>By chat type</h2>
              {metrics.by_chat_type.map((row) => (
                <MixBar
                  key={row.chat_type}
                  label={typeLabel(row.chat_type)}
                  count={row.count}
                  total={metrics.bookmark_count}
                  className={
                    row.chat_type === 'main'
                      ? 'bg-sky-500'
                      : row.chat_type === 'custom'
                        ? 'bg-violet-500'
                        : row.chat_type === 'expense-dispute'
                          ? 'bg-amber-500'
                          : 'bg-orange-500'
                  }
                />
              ))}
            </section>
            <section className='rounded-lg border border-gray-200 bg-white p-4 shadow-sm space-y-3'>
              <h2 className='text-sm font-semibold text-gray-900'>By content type</h2>
              {metrics.by_content_type.length === 0 ? (
                <p className='text-sm text-gray-500'>No bookmarks yet.</p>
              ) : (
                metrics.by_content_type.map((row) => (
                  <MixBar
                    key={row.content_type}
                    label={row.content_type}
                    count={row.count}
                    total={metrics.bookmark_count}
                    className='bg-primary'
                  />
                ))
              )}
            </section>
            <section className='rounded-lg border border-gray-200 bg-white p-4 shadow-sm'>
              <h2 className='text-sm font-semibold text-gray-900'>Custom folder names</h2>
              <p className='mt-1 text-xs text-gray-500'>
                What people name folders (not the default “Bookmarks”).
              </p>
              {metrics.custom_folder_names.length === 0 ? (
                <p className='mt-3 text-sm text-gray-500'>No custom folders yet.</p>
              ) : (
                <ul className='mt-3 divide-y divide-gray-100'>
                  {metrics.custom_folder_names.map((row) => (
                    <li
                      key={row.name}
                      className='flex items-baseline justify-between gap-3 py-2 text-sm'
                    >
                      <span className='font-medium text-gray-800 truncate'>{row.name}</span>
                      <span className='shrink-0 tabular-nums text-gray-600'>
                        {row.bookmark_count.toLocaleString()} saved ·{' '}
                        {row.folder_count.toLocaleString()} folders
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>

          <section className='bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden'>
            <div className='px-4 py-3 border-b border-gray-200'>
              <h2 className='text-lg font-semibold text-gray-900'>Top bookmarkers</h2>
              <p className='text-xs text-gray-500 mt-1'>People with the most saved messages.</p>
            </div>
            {metrics.top_users.length === 0 ? (
              <p className='px-4 py-8 text-sm text-gray-500'>No bookmarks yet.</p>
            ) : (
              <div className='overflow-x-auto'>
                <table className='min-w-full text-sm'>
                  <thead className='bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500'>
                    <tr>
                      <th className='px-4 py-2'>User</th>
                      <th className='px-4 py-2'>Bookmarks</th>
                      <th className='px-4 py-2'>Custom folders</th>
                      <th className='px-4 py-2'>Household</th>
                    </tr>
                  </thead>
                  <tbody className='divide-y divide-gray-100'>
                    {metrics.top_users.map((user) => (
                      <tr key={user.user_id}>
                        <td className='px-4 py-2'>
                          <Link
                            to='/admin/users/$userId'
                            params={{ userId: String(user.user_id) }}
                            search={{
                              hid:
                                user.household_id != null
                                  ? String(user.household_id)
                                  : undefined,
                            }}
                            className={ADMIN_USER_LINK_CLASS}
                          >
                            {authorLabel(user, user.user_id)}
                          </Link>
                          {user.email ? (
                            <p className='mt-1 text-xs text-gray-500'>{user.email}</p>
                          ) : null}
                        </td>
                        <td className='px-4 py-2 tabular-nums font-semibold text-gray-900'>
                          {user.bookmark_count.toLocaleString()}
                        </td>
                        <td className='px-4 py-2 tabular-nums text-gray-700'>
                          {user.custom_folder_count.toLocaleString()}
                        </td>
                        <td className='px-4 py-2 tabular-nums text-gray-700'>
                          {user.household_id ?? '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      ) : null}

      <section className='bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden'>
        <div className='px-4 py-3 border-b border-gray-200 space-y-3'>
          <div>
            <h2 className='text-lg font-semibold text-gray-900'>Recent bookmarks</h2>
            <p className='text-xs text-gray-500 mt-1'>
              Newest first. Preview is truncated; full message bodies stay in Chats.
            </p>
          </div>
          <div className='flex flex-wrap gap-4 items-end'>
            <div>
              <label className='block text-xs font-semibold text-gray-600 mb-1'>
                Household ID
              </label>
              <input
                value={householdFilter}
                onChange={(e) => {
                  setHouseholdFilter(e.target.value);
                  setPage(0);
                }}
                className='px-2 py-1 border border-gray-300 rounded text-sm w-28'
                placeholder='e.g. 12'
                inputMode='numeric'
              />
            </div>
            <div>
              <label className='block text-xs font-semibold text-gray-600 mb-1'>
                Chat type
              </label>
              <select
                value={chatTypeFilter}
                onChange={(e) => {
                  setChatTypeFilter(e.target.value as AdminChatType | '');
                  setPage(0);
                }}
                className='px-2 py-1 border border-gray-300 rounded text-sm'
              >
                <option value=''>All</option>
                <option value='main'>Main</option>
                <option value='custom'>Custom</option>
                <option value='expense-dispute'>Expense dispute</option>
                <option value='payment-dispute'>Payment dispute</option>
              </select>
            </div>
          </div>
        </div>

        {listQuery.isLoading && !listQuery.data ? (
          <div className='flex justify-center py-12'>
            <div className='animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full' />
          </div>
        ) : listQuery.isError ? (
          <p className='px-4 py-6 text-sm text-red-600'>Could not load bookmarks.</p>
        ) : rows.length === 0 ? (
          <p className='px-4 py-8 text-sm text-gray-500'>No bookmarks match these filters.</p>
        ) : (
          <>
            <div className='overflow-x-auto'>
              <table className='min-w-full text-sm'>
                <thead className='bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500'>
                  <tr>
                    <th className='px-4 py-2'>When</th>
                    <th className='px-4 py-2'>User</th>
                    <th className='px-4 py-2'>Folder</th>
                    <th className='px-4 py-2'>Chat</th>
                    <th className='px-4 py-2'>Preview</th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-gray-100'>
                  {rows.map((row: AdminBookmarkRow) => (
                    <tr key={row.id}>
                      <td className='px-4 py-2 whitespace-nowrap text-xs text-gray-600'>
                        {formatPacificDateTime(row.created_at)}
                      </td>
                      <td className='px-4 py-2'>
                        <Link
                          to='/admin/users/$userId'
                          params={{ userId: String(row.user_id) }}
                          search={{ hid: String(row.household_id) }}
                          className={ADMIN_USER_LINK_CLASS}
                        >
                          {authorLabel(row.author, row.user_id)}
                        </Link>
                        <p className='mt-1 text-xs text-gray-500'>HH {row.household_id}</p>
                      </td>
                      <td className='px-4 py-2'>
                        <span className='font-medium text-gray-900'>{row.folder_name}</span>
                        {row.folder_is_default ? (
                          <p className='text-xs text-gray-500'>Default</p>
                        ) : null}
                      </td>
                      <td className='px-4 py-2'>
                        <span
                          className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold ${typeBadgeClass(row.chat_type)}`}
                        >
                          {typeLabel(row.chat_type)}
                        </span>
                        <p className='mt-1 text-xs text-gray-500'>{row.chat_title}</p>
                      </td>
                      <td className='px-4 py-2 max-w-md text-gray-800'>
                        {row.preview ?? (
                          <span className='text-gray-500 italic'>{row.content_type}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 ? (
              <div className='flex items-center justify-between px-4 py-3 border-t border-gray-200 text-sm'>
                <p className='text-gray-600 tabular-nums'>
                  {total.toLocaleString()} total · page {page + 1} of {totalPages}
                </p>
                <div className='flex gap-2'>
                  <button
                    type='button'
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0 || listQuery.isFetching}
                    className='px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50'
                  >
                    Previous
                  </button>
                  <button
                    type='button'
                    onClick={() => setPage((p) => p + 1)}
                    disabled={page >= totalPages - 1 || listQuery.isFetching}
                    className='px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50'
                  >
                    Next
                  </button>
                </div>
              </div>
            ) : null}
          </>
        )}
      </section>
    </div>
  );
}

export const Route = createFileRoute('/admin/bookmarks')({
  validateSearch: (search: Record<string, unknown>) => ({
    range:
      typeof search.range === 'string' && ALLOWED_RANGES.has(search.range)
        ? (search.range as AdminBookmarkMetricsRange)
        : '30d',
  }),
  component: AdminBookmarksPage,
});
